import { useEffect, useState } from "react";
import PageBreadcrumbs from "../layouts/PageBreadcrumbs.js";
import { SkeletonTable } from "../components/Skeleton.js";
import { api, ApiError, TenantWorkspace, WorkspaceMember } from "../lib/api.js";
import { useTableState } from "../lib/useTableState.js";
import TableToolbar from "../components/TableToolbar.js";

function rolePill(role?: string) {
  if (!role) return <span className="pill pill-muted">—</span>;
  if (role === "admin")   return <span className="pill pill-ok">Admin</span>;
  if (role === "analyst") return <span className="pill pill-info">Analyst</span>;
  return <span className="pill pill-muted">{role}</span>;
}

function statusPill(status?: string) {
  if (!status) return <span className="pill pill-muted">—</span>;
  const s = status.toLowerCase();
  if (s === "active")    return <span className="pill pill-ok">Active</span>;
  if (s === "disabled" || s === "inactive") return <span className="pill pill-muted">{status}</span>;
  if (s === "pending")   return <span className="pill pill-warn">Pending</span>;
  return <span className="pill pill-info">{status}</span>;
}

export default function Workspaces() {
  const [workspaces, setWorkspaces] = useState<TenantWorkspace[]>([]);
  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const tWorkspaces = useTableState(workspaces as unknown as Array<Record<string, unknown>>, {
    searchFields: ["id", "name", "slug", "status", "ownerUserId"],
    initialPageSize: 25
  });
  const tMembers = useTableState(members as unknown as Array<Record<string, unknown>>, {
    searchFields: ["id", "userId", "name", "workspaceId", "role", "status", "email"],
    initialPageSize: 25
  });

  useEffect(() => {
    let cancelled = false;
    api.workspace()
      .then((d) => { if (!cancelled) { setWorkspaces(d.workspaces ?? []); setMembers(d.members ?? []); } })
      .catch((e: unknown) => { if (!cancelled) setError(e instanceof ApiError ? e.message : String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>Workspaces</h1>
          <p>Tenant workspaces and their members.</p>
        </div>
      </header>

      {error && <div className="alert alert-error">{error}</div>}

      <section className="section">
        <div className="section-head">
          <h2>Workspaces</h2>
          <span className="soft">{workspaces.length} total</span>
        </div>
        {loading ? <SkeletonTable rows={5} columns={4} /> :
         workspaces.length === 0 ? <div className="empty">No workspaces yet.</div> : (
          <div className="table-scroll">
            <TableToolbar
              search={tWorkspaces.search} setSearch={tWorkspaces.setSearch}
              page={tWorkspaces.page} setPage={tWorkspaces.setPage}
              pageSize={tWorkspaces.pageSize} setPageSize={tWorkspaces.setPageSize}
              totalPages={tWorkspaces.totalPages} totalRows={tWorkspaces.totalRows}
              hideExport
              searchPlaceholder="Search workspaces…"
            />
            <table className="data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Slug</th>
                  <th>Status</th>
                  <th>Owner</th>
                  <th>Members</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {(tWorkspaces.paged as unknown as TenantWorkspace[]).map((w) => (
                  <tr key={w.id}>
                    <td><code>{w.id}</code></td>
                    <td>{w.name ?? "—"}</td>
                    <td>{w.slug ? <code>{w.slug}</code> : "—"}</td>
                    <td>{statusPill(w.status)}</td>
                    <td className="soft">{w.ownerUserId ?? "—"}</td>
                    <td>{typeof w.memberCount === "number" ? w.memberCount : "—"}</td>
                    <td className="soft">{w.createdAt ? new Date(w.createdAt).toLocaleDateString() : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
         )}
      </section>

      <section className="section">
        <div className="section-head">
          <h2>Members</h2>
          <span className="soft">{members.length} total</span>
        </div>
        {loading ? <SkeletonTable rows={5} columns={4} /> :
         members.length === 0 ? <div className="empty">No members in this scope.</div> : (
          <div className="table-scroll">
            <TableToolbar
              search={tMembers.search} setSearch={tMembers.setSearch}
              page={tMembers.page} setPage={tMembers.setPage}
              pageSize={tMembers.pageSize} setPageSize={tMembers.setPageSize}
              totalPages={tMembers.totalPages} totalRows={tMembers.totalRows}
              hideExport
              searchPlaceholder="Search members…"
            />
            <table className="data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>User</th>
                  <th>Workspace</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Email</th>
                </tr>
              </thead>
              <tbody>
                {(tMembers.paged as unknown as WorkspaceMember[]).map((m) => (
                  <tr key={m.id}>
                    <td><code>{m.id}</code></td>
                    <td>{m.name ?? m.userId ?? "—"}</td>
                    <td>{m.workspaceId ? <code>{m.workspaceId}</code> : "—"}</td>
                    <td>{rolePill(m.role)}</td>
                    <td>{statusPill(m.status)}</td>
                    <td className="soft">{m.email ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
         )}
      </section>
    </div>
  );
}
